import React, { useEffect, useMemo, useState } from "react";
import { fetchJson } from "../../lib/api";
import { flattenChartAccounts } from "../../lib/chart-utils";
import { formatAmount } from "../../lib/format-utils";
import type { ChartOfAccountsResponse } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";

export const ChartOfAccountsView = ({
  houseId,
  search,
  setSearch,
  navigate,
}: {
  houseId: number | null;
  search: string;
  setSearch: (nextSearch: string, options?: { replace?: boolean }) => void;
  navigate: (path: string, search?: string) => void;
}) => {
  const { t } = useI18n();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [chart, setChart] = useState<ChartOfAccountsResponse | null>(null);
  const [isLoadingChart, setIsLoadingChart] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!houseId) {
      setChart(null);
      return;
    }
    const yearParam = Number.parseInt(searchParams.get("year") ?? "", 10);
    const yearQuery = Number.isNaN(yearParam) ? "" : `?year=${yearParam}`;
    setIsLoadingChart(true);
    setError(null);
    fetchJson<ChartOfAccountsResponse>(`/api/houses/${houseId}/chart-of-accounts${yearQuery}`)
      .then((response) => {
        setChart(response);
        if (Number.isNaN(yearParam) || response.year !== yearParam) {
          const nextParams = new URLSearchParams(search);
          nextParams.set("year", String(response.year));
          setSearch(`?${nextParams.toString()}`, { replace: true });
        }
      })
      .catch((loadError) => {
        setError(
          loadError instanceof Error
            ? loadError.message
            : t("accounts.loadFailed", "Failed to load accounts"),
        );
      })
      .finally(() => {
        setIsLoadingChart(false);
      });
  }, [houseId, search, searchParams, setSearch]);

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("accounts.title", "Chart of accounts")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("accounts.selectHouse", "Select a house to review the chart of accounts.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const availableYears = chart?.years.length ? chart.years : chart ? [chart.year] : [];
  const activeYear = chart?.year ?? new Date().getUTCFullYear();
  const flattenedRows = chart ? flattenChartAccounts(chart.accounts) : [];

  const updateYear = (value: string) => {
    const nextParams = new URLSearchParams(search);
    nextParams.set("year", value);
    setSearch(`?${nextParams.toString()}`);
  };

  const openAccountJournalEntries = (accountId: number) => {
    const nextParams = new URLSearchParams(search);
    nextParams.set("account", String(accountId));
    navigate("/journal-entries", `?${nextParams.toString()}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-lg font-semibold text-ink">{t("accounts.title", "Chart of accounts")}</p>
          <p className="text-sm text-muted">
            {t("accounts.subtitle", "Review balances and drill into account journal entries.")}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-ink" htmlFor="coa-year">
              {t("common.year", "Year")}
            </label>
            <select
              id="coa-year"
              className="h-9 rounded-md border px-3 text-sm brand-input brand-focus"
              value={String(activeYear)}
              onChange={(event) => updateYear(event.target.value)}
              disabled={!availableYears.length}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {isLoadingChart ? (
        <p className="text-sm text-muted">{t("accounts.loading", "Loading accounts...")}</p>
      ) : null}
      {error ? <p className="text-sm text-error">{error}</p> : null}
      <Card>
        <CardHeader>
          <CardTitle>{t("accounts.listTitle", "Accounts")}</CardTitle>
        </CardHeader>
        <CardContent>
          {!chart ? (
            <p className="text-sm text-muted">
              {t("accounts.noChart", "No chart data available.")}
            </p>
          ) : flattenedRows.length === 0 ? (
            <p className="text-sm text-muted">
              {t("accounts.empty", "No accounts configured yet.")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[160px]">{t("accounts.account", "Account")}</TableHead>
                  <TableHead>{t("accounts.name", "Name")}</TableHead>
                  <TableHead className="text-right">{t("accounts.balance", "Balance")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flattenedRows.map(({ node, depth }) => {
                  const isClickable = Boolean(node.id);
                  const label = node.labelKey ? t(node.labelKey, node.name) : node.name;
                  return (
                    <TableRow
                      key={node.code}
                      className={isClickable ? "cursor-pointer hover:bg-slate-50" : undefined}
                      onClick={() => {
                        if (node.id) {
                          openAccountJournalEntries(node.id);
                        }
                      }}
                    >
                      <TableCell className={node.isTotal ? "font-semibold" : undefined}>
                        {node.code}
                      </TableCell>
                      <TableCell
                        className={node.isTotal ? "font-semibold" : undefined}
                        style={{ paddingLeft: depth * 20 }}
                      >
                        {label}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatAmount(node.balance, node.currency)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
